import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FiCalendar, FiClock, FiCheckCircle } from 'react-icons/fi';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { EmptyState } from '@/components/custom/empty-state';
import { useTranslation } from 'react-i18next';
import { confirm } from '@/lib/confirm';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { CourseAboutDescription, CourseAboutTitle, CourseBanner, CourseBannerCTA, CourseBannerDescription, CourseBannerTitle } from '@/components/custom/course';
import { Button } from '@/components/ui/button';
import { Widget, WidgetBody, WidgetHeader, WidgetSubtitle, WidgetText, WidgetTitle } from '@/components/custom/widget';
import { Badge } from '@/components/ui/badge';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { CardKpi, CardKpiGrid } from '@/components/ui/card';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { PlayfulExamsAbout } from '@/pages/exams/playful-exams-about';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SimpleDiscursiveQuestionCard from './exam-correction/simple-discursive-question-card';

export function ExamsAbout({ uri }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const client = useClient();
  const { id } = useParams();
  const playfulStudent = usePlayfulStudent();
  const [exam, setExam] = useState([]);
  const [courses, setCourses] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const [notAllowed, setNotAllowed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [lastAttemptResult, setLastAttemptResult] = useState([]);
  const [loadingDiscursive, setLoadingDiscursive] = useState(false);

  const [examQuestions, setExamQuestions] = useState([]);

  const getCourses = async () => {
    try {
      const { data } = await client.fetch('courses');
      setCourses(data);
    } catch {
      toast.error(t('toast.errorGetCourse'));
    }
  };

  const getResults = async () => {
    setIsLoading(true);
    const { data } = await client.fetch(`question_books_users?filter[current_user]=true&filter[question_book_id]=${id}`);

    setResults(data);
    setIsLoading(false);
  };

  const getLastDiscursiveResult = async () => {
    setLoadingDiscursive(true);

    try {
      const { data } = await client.fetch(`question_books_users/discursive_answers?question_book_id=${id}`);
      setLastAttemptResult(data);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar questões discursivas');
    } finally {
      setLoadingDiscursive(false);
    }
  };

  const getExam = async () => {
    const { data, error } = await client.fetch(`question_books/${id}`);
    if (error) {
      toast.error(t('toast.errorGetExam'));
      setNotAllowed(true);
    } else {
      setExam(data);
      setTaxonomies(data.taxonomies);
    }

    setIsLoading(false);
  };

  const getExamQuestions = async () => {
    const { data, error } = await client.fetch(`questions?filter[question_book_id]=${id}`);
    if (error) {
      toast.error(t('toast.errorGetExam'));
      setNotAllowed(true);
    } else {
      setExamQuestions(data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getCourses();
    getExam();
    getResults();
  }, []);

  const approved = results.filter(result => result?.status === 'approved');
  const hasExamWaitingCorrection = results.filter(result => result?.status === 'waiting_correction');
  const hasExamInCorrection = results.filter(result => result?.status === 'review');

  const lastAttempt = results.sort((a, b) => new Date(b?.['finished-at']) - new Date(a?.['finished-at']))[0];

  const now = new Date();

  const isAvaliable = (new Date(exam?.['published-at']) <= now && now <= new Date(exam?.['finished-at'])) || (!exam?.['finished-at'] && new Date(exam?.['published-at']) <= now);

  const courseName = courses.filter(x => x.id === exam?.['course-ids']?.[0]);

  function formatDateTime(dateTimeString) {
    if (!dateTimeString) return '';

    const dateTime = new Date(dateTimeString);
    const hours = dateTime.getHours().toString().padStart(2, '0');
    const minutes = dateTime.getMinutes().toString().padStart(2, '0');
    return `${hours}h${minutes}`;
  }

  const hours = exam?.['resolution-time'] ? parseInt(exam['resolution-time'].split(':')[0], 10) : 0;

  const warningStart = async () => {
    const confirmed = await confirm({
      title: t('warning.warningStartExams', { resolutionTime: exam?.['resolution-time'] }),
      description: t('warning.warningStartExams2'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      navigate(`/provas/${id}`);
    }
  };

  const breadcrumbs = {
    title: 'Avaliações e Questões',
    nav: [
      {
        route: '/avaliacoes',
        name: 'Provas',
        isActive: false
      },
      {
        route: uri,
        name: 'Sobre',
        isActive: true
      }
    ]
  };

  if (playfulStudent) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        {notAllowed ? (
          <EmptyState
            type="data"
            title={t('emptyState.routeNoAccessTitle')}
            text={t('emptyState.contactAnAdministratorText')}
          />
        ) : isLoading ? (
          <Loader />
        ) : (
          <PlayfulExamsAbout
            exam={exam}
            courseName={courseName[0]?.title}
            taxonomies={taxonomies}
            approved={approved}
            isAvaliable={isAvaliable}
            onStart={() => navigate(`/provas/${id}`)}
          />
        )}
      </DashboardContent>
    );
  }

  return !notAllowed ? (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <CourseBanner className="mb-12">
            <CourseBannerTitle>{exam.title}</CourseBannerTitle>
            <CourseBannerDescription asChild>
              {approved?.length > 0 && !exam?.['able-to-retry'] ? ( //Se o aluno ja foi aprovado e não esta disponivel para repetir
                <div>
                  <p className="mb-4">Esta avaliação ja foi concluida e não pode ser repetida.</p>
                  <p className="mb-4">Veja o seu resultado no botão abaixo!</p>
                </div>
              ) : (
                //Não realizou a prova ainda ou foi reprovado ou esta disponivel para repetir
                <div>
                  <p className="mb-4">{t('exams.examBannerDescription')}</p>
                  <p className="mb-4">{t('exams.examBannerDescriptionWaterAndBathroom')}</p>
                </div>
              )}
            </CourseBannerDescription>

            <CourseBannerCTA>
              {/* Regra para refazer a prova:
              - Prova deve estar dentro do período de realização
              - Aluno deve ter finalizado a prova (is-question-book-done = true)
              - Deve ter opção de refazer (able-to-retry = true)
              - Aluno deve ter sido reprovado (approved.length === 0)
              - Aluno não pode ter prova esperando correção (hasExamWaitingCorrection.length === 0) ou que esteja em correção (hasExamInCorrection.length === 0)
              */}

              {isAvaliable && exam?.['is-question-book-done'] && exam?.['able-to-retry'] && approved?.length === 0 && hasExamWaitingCorrection.length === 0 && hasExamInCorrection.length === 0 ? (
                <Button
                  wide
                  title={exam?.title}
                  onClick={warningStart}
                >
                  Refazer prova
                </Button>
              ) : isAvaliable && !exam?.['is-question-book-done'] ? ( //Prova ainda não realizada
                <Button
                  wide
                  title={exam?.title}
                  onClick={warningStart}
                >
                  {t('button.startExam')}
                </Button>
              ) : (
                isAvaliable &&
                approved?.length > 0 && (
                  <Button asChild>
                    <Link to={`/avaliacoes/meus-resultados`}>{t('exams.thResults')}</Link>
                  </Button>
                )
              )}
            </CourseBannerCTA>
          </CourseBanner>

          <Page>
            <PageContent bg>
              <div className="flex w-full flex-wrap items-center justify-between gap-3">
                {isAvaliable && exam?.['is-question-book-done'] && approved?.length === 0 && exam?.['able-to-retry'] && hasExamWaitingCorrection.length === 0 && hasExamInCorrection.length === 0 && (
                  <Badge variant="danger">Você não foi aprovado. Refaça a prova para melhorar seu desempenho</Badge>
                )}
                {/* Botão de comentários do professor disponível se:
                - Prova dentro do período de realização
                - Prova tiver questão discursiva (has-discursive)
                - Último envio tiver comentários do professor (lastAttempt?.['has-discursive-feedback'])
                - Último envio tiver sido reprovado (lastAttempt.status === 'disapproved')
                 */}
                {isAvaliable && exam?.['has-discursive'] && lastAttempt?.['has-discursive-feedback'] && lastAttempt?.status === 'disapproved' && (
                  <Button
                    variant="outline"
                    className="ml-auto"
                    onClick={() => {
                      getLastDiscursiveResult();
                      getExamQuestions();
                      setShowModal(true);
                    }}
                  >
                    Comentários do professor
                  </Button>
                )}
              </div>

              <div>
                <Badge
                  size="xs"
                  square
                  className="bg-[#D6FDFF] text-neutral-dark"
                >
                  {courseName[0]?.title}
                </Badge>
                <CourseAboutTitle>{t('exams.titleDescription')}</CourseAboutTitle>
                <CourseAboutDescription>{exam.description}</CourseAboutDescription>

                {exam?.['published-at'] && exam?.['finished-at'] && (
                  <CardKpi className="mb-3">
                    <FiCalendar />{' '}
                    <span className="font-light">
                      {t('exams.from')}
                      <strong> {moment(exam?.['published-at']).format(t('datesForm.formatDate'))}</strong> {t('exams.at')} <strong>{formatDateTime(exam?.['published-at'])}</strong> {t('exams.to')}
                      <strong> {moment(exam?.['finished-at']).format(t('datesForm.formatDate'))}</strong> {t('exams.at')} <strong>{formatDateTime(exam?.['finished-at'])}</strong>
                    </span>
                  </CardKpi>
                )}
                <CardKpiGrid>
                  <CardKpi>
                    <FiClock />

                    {hours > 1 ? (
                      <span
                        className="font-light"
                        dangerouslySetInnerHTML={{ __html: t('exams.hoursDuration', { hours: hours }) }}
                      />
                    ) : (
                      <span
                        className="font-light"
                        dangerouslySetInnerHTML={{ __html: t('exams.hourDuration', { hours: hours }) }}
                      />
                    )}
                  </CardKpi>
                  <CardKpi>
                    <FiCheckCircle />
                    <span className="font-light">
                      <strong>{exam?.['question-count']}</strong> {t('exams.questions')}
                    </span>
                  </CardKpi>
                </CardKpiGrid>
              </div>
            </PageContent>

            <PageSidebar>
              <Widget>
                <WidgetHeader>
                  <WidgetTitle>{t('exams.titleMaterialsDistribuition')}</WidgetTitle>
                  <WidgetSubtitle>
                    {exam?.['question-count']} {t('exams.questions')}
                  </WidgetSubtitle>
                </WidgetHeader>

                <WidgetBody>
                  {taxonomies?.map(taxonomy => (
                    <div
                      className="flex items-center justify-between gap-4 border-b border-solid border-bright py-3 last:border-0"
                      key={taxonomy.id}
                      title={taxonomy.name}
                    >
                      {taxonomy.name.length > 25 ? (
                        <Badge
                          variant="primary-light"
                          size="xs"
                        >
                          {taxonomy.name.slice(0, 25)}...
                        </Badge>
                      ) : (
                        <Badge
                          variant="primary-light"
                          size="xs"
                        >
                          {taxonomy.name}
                        </Badge>
                      )}

                      <WidgetText>
                        {taxonomy.questionCount} {t('exams.questions')}
                      </WidgetText>
                    </div>
                  ))}
                </WidgetBody>
              </Widget>
            </PageSidebar>
          </Page>

          <Dialog
            open={showModal}
            onOpenChange={setShowModal}
          >
            <DialogContent size="md">
              <DialogHeader>
                <DialogTitle>Comentários do professor</DialogTitle>
              </DialogHeader>
              {loadingDiscursive ? (
                <Loader />
              ) : (
                <>
                  {lastAttemptResult?.['discursive-answers']
                    ?.filter(question => question.feedback !== null)
                    .map(question => (
                      <SimpleDiscursiveQuestionCard
                        question={question}
                        examQuestions={examQuestions}
                      />
                    ))}
                </>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </DashboardContent>
  ) : (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <EmptyState
        type="data"
        title={t('emptyState.routeNoAccessTitle')}
        text={t('emptyState.contactAnAdministratorText')}
      />
    </DashboardContent>
  );
}
